import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

import {

  fetchCourseDays,

  createCourseDay,

  updateCourseDay,

  deleteCourseDay,

} from "../../redux/slices/courseSlice";

import { pickLocalized } from "../../lib/localized";

import {

  asMultilingual,

  hasEnglishText,

  pruneMultilingual,

} from "../../lib/multilingual-helpers";

import { Button } from "../../components/ui/button";

import { Input } from "../../components/ui/input";

import { Label } from "../../components/ui/label";

import { Badge } from "../../components/ui/badge";

import {

  Card,

  CardContent,

  CardDescription,

  CardHeader,

  CardTitle,

} from "../../components/ui/card";

import {

  Table,

  TableBody,

  TableCell,

  TableHead,

  TableHeader,

  TableRow,

} from "../../components/ui/table";

import {

  Dialog,

  DialogContent,

  DialogFooter,

  DialogHeader,

  DialogTitle,

} from "../../components/ui/dialog";

import { useToast } from "../../components/ui/use-toast";

import FileOrLinkField from "../../components/files/FileOrLinkField";

import { MultilingualInput } from "../../components/common/MultilingualInput";

import QuestionPicker from "../../components/questions/QuestionPicker";



function emptyDayForm() {

  return {

    day_number: "",

    title: { en: "" },

    material_url: "",

    total_time_limit_minutes: "",

    questions: [],

  };

}



function mapDayQuestions(day) {

  const qs = day?.questions || [];

  return qs

    .map((q) => {

      const junction = q.CourseDayQuestion || q.course_day_questions || {};

      return {

        question_id: q.id,

        order_number: junction.order_number ?? 0,

        marks_override: junction.marks_override ?? null,

        question_text: q.question_text,

        question_type: q.question_type,

        default_marks: q.default_marks,

      };

    })

    .sort((a, b) => a.order_number - b.order_number);

}



function CourseDaysPanel({ courseId }) {

  const dispatch = useDispatch();

  const { toast } = useToast();

  const { courseDays } = useSelector((s) => s.course);

  const [dialogOpen, setDialogOpen] = useState(false);

  const [editingDay, setEditingDay] = useState(null);

  const [form, setForm] = useState(emptyDayForm());

  const [saving, setSaving] = useState(false);



  useEffect(() => {

    if (courseId) dispatch(fetchCourseDays(courseId));

  }, [dispatch, courseId]);



  const openCreate = () => {

    setEditingDay(null);

    setForm({

      ...emptyDayForm(),

      day_number: String((courseDays?.length || 0) + 1),

    });

    setDialogOpen(true);

  };



  const openEdit = (day) => {

    setEditingDay(day);

    setForm({

      day_number: String(day.day_number),

      title: asMultilingual(day.title),

      material_url: day.material_url || "",

      total_time_limit_minutes: day.total_time_limit_minutes

        ? String(day.total_time_limit_minutes)

        : "",

      questions: mapDayQuestions(day),

    });

    setDialogOpen(true);

  };



  const handleSave = async (e) => {

    e.preventDefault();

    const dayNumber = parseInt(form.day_number, 10);

    if (Number.isNaN(dayNumber) || dayNumber < 1) {

      toast({

        title: "Validation",

        description: "Day number must be at least 1",

        variant: "destructive",

      });

      return;

    }

    if (!hasEnglishText(form.title)) {

      toast({

        title: "Validation",

        description: "English title is required",

        variant: "destructive",

      });

      return;

    }



    const payload = {

      day_number: dayNumber,

      title: pruneMultilingual(form.title),

      material_url: form.material_url.trim() || null,

      total_time_limit_minutes: form.total_time_limit_minutes

        ? parseInt(form.total_time_limit_minutes, 10)

        : null,

      questions: form.questions.map((q, index) => ({

        question_id: q.question_id,

        order_number: index + 1,

        marks_override: q.marks_override ?? null,

      })),

    };



    setSaving(true);

    try {

      if (editingDay) {

        await dispatch(

          updateCourseDay({ dayId: editingDay.id, data: payload }),

        ).unwrap();

        toast({ title: "Day updated" });

      } else {

        await dispatch(

          createCourseDay({ courseId, data: payload }),

        ).unwrap();

        toast({ title: "Day created" });

      }

      setDialogOpen(false);

      dispatch(fetchCourseDays(courseId));

    } catch (err) {

      toast({

        title: "Error",

        description: typeof err === "string" ? err : "Save failed",

        variant: "destructive",

      });

    } finally {

      setSaving(false);

    }

  };



  const handleDelete = async (day) => {

    if (!window.confirm(`Delete day ${day.day_number}?`)) return;

    try {

      await dispatch(deleteCourseDay(day.id)).unwrap();

      toast({ title: "Day deleted" });

      dispatch(fetchCourseDays(courseId));

    } catch (err) {

      toast({

        title: "Error",

        description: typeof err === "string" ? err : "Delete failed",

        variant: "destructive",

      });

    }

  };



  const days = [...(courseDays || [])].sort(

    (a, b) => a.day_number - b.day_number,

  );



  return (

    <>

      <Card>

        <CardHeader className="flex flex-row items-center justify-between">

          <div>

            <CardTitle>Course days</CardTitle>

            <CardDescription>

              Build each day with lesson material, quiz questions, and optional

              time limits.

            </CardDescription>

          </div>

          <Button type="button" size="sm" onClick={openCreate}>

            <Plus className="w-4 h-4 mr-1" />

            Add day

          </Button>

        </CardHeader>

        <CardContent>

          {days.length === 0 ? (

            <p className="text-sm text-muted-foreground py-6">

              No syllabus days yet. Add at least one day per course duration.

            </p>

          ) : (

            <Table>

              <TableHeader>

                <TableRow>

                  <TableHead>#</TableHead>

                  <TableHead>Title</TableHead>

                  <TableHead>Questions</TableHead>

                  <TableHead>Material</TableHead>

                  <TableHead className="text-right">Actions</TableHead>

                </TableRow>

              </TableHeader>

              <TableBody>

                {days.map((d) => (

                  <TableRow key={d.id}>

                    <TableCell>{d.day_number}</TableCell>

                    <TableCell>{pickLocalized(d.title) || "—"}</TableCell>

                    <TableCell>

                      <Badge variant="secondary">

                        {(d.questions?.length ?? 0) === 0

                          ? "None"

                          : `${d.questions.length} question${d.questions.length === 1 ? "" : "s"}`}

                      </Badge>

                    </TableCell>

                    <TableCell className="max-w-xs truncate text-xs">

                      {d.material_url ? "Attached" : "—"}

                    </TableCell>

                    <TableCell className="text-right space-x-1">

                      <Button

                        type="button"

                        variant="ghost"

                        size="sm"

                        onClick={() => openEdit(d)}

                      >

                        <Pencil className="w-4 h-4" />

                      </Button>

                      <Button

                        type="button"

                        variant="ghost"

                        size="sm"

                        onClick={() => handleDelete(d)}

                      >

                        <Trash2 className="w-4 h-4 text-destructive" />

                      </Button>

                    </TableCell>

                  </TableRow>

                ))}

              </TableBody>

            </Table>

          )}

        </CardContent>

      </Card>



      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>

        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">

          <DialogHeader>

            <DialogTitle>

              {editingDay ? `Edit day ${editingDay.day_number}` : "New course day"}

            </DialogTitle>

          </DialogHeader>

          <form onSubmit={handleSave} className="grid gap-4">

            <div className="grid gap-2">

              <Label>Day number</Label>

              <Input

                type="number"

                min={1}

                value={form.day_number}

                onChange={(e) =>

                  setForm((f) => ({ ...f, day_number: e.target.value }))

                }

                disabled={!!editingDay}

              />

            </div>

            <MultilingualInput

              label="Day title"

              value={form.title}

              onChange={(title) => setForm((f) => ({ ...f, title }))}

              required

            />

            <FileOrLinkField

              label="Lesson video or material"

              hint="Upload a PDF, image, or video file — or link to YouTube."

              value={form.material_url}

              onChange={(url) =>

                setForm((f) => ({ ...f, material_url: url }))

              }

            />

            <div className="grid gap-2">

              <Label>Quiz time limit (minutes, optional)</Label>

              <Input

                type="number"

                min={1}

                value={form.total_time_limit_minutes}

                onChange={(e) =>

                  setForm((f) => ({

                    ...f,

                    total_time_limit_minutes: e.target.value,

                  }))

                }

              />

            </div>

            <QuestionPicker

              value={form.questions}

              onChange={(questions) =>

                setForm((f) => ({ ...f, questions }))

              }

            />

            <DialogFooter>

              <Button type="submit" disabled={saving}>

                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}

                Save day

              </Button>

            </DialogFooter>

          </form>

        </DialogContent>

      </Dialog>

    </>

  );

}



export default CourseDaysPanel;

